/**
 * Adobe Spectrum Theme (Light Mode)
 * Enterprise design system theme inspired by Adobe Spectrum 2
 * Primary: Blue (#1D7FD3), Secondary: Red (#DA1C20), Accent: Green (#2D8659)
 */

import { adobeColors } from '../tokens/colors.js';
import { spacing, zIndex } from '../tokens/spacing.js';
import { typography } from '../tokens/typography.js';
import { borderRadius, shadows, transitions } from '../tokens/effects.js';

export const adobeTheme = {
  name: 'adobe',
  label: 'Adobe Spectrum',
  description: 'Enterprise-grade theme inspired by Adobe Spectrum 2 design system',
  isDark: false,

  // Color palette
  colors: {
    primary: adobeColors.primary,
    primaryLight: adobeColors.primaryLight,
    primaryDark: adobeColors.primaryDark,
    primaryHover: adobeColors.primaryDark,
    primaryOpacity: adobeColors.primaryOpacity20,

    secondary: adobeColors.secondary,
    secondaryLight: adobeColors.secondaryLight,
    secondaryDark: adobeColors.secondaryDark,
    secondaryOpacity: adobeColors.secondaryOpacity20,

    accent: adobeColors.accent,
    accentLight: adobeColors.accentLight,
    accentDark: adobeColors.accentDark,
    accentOpacity: adobeColors.accentOpacity20,

    // Semantic colors
    error: adobeColors.error,
    errorLight: adobeColors.errorLight,
    success: adobeColors.success,
    successLight: adobeColors.successLight,
    warning: adobeColors.warning,
    warningLight: adobeColors.warningLight,
    info: adobeColors.info,
    infoLight: adobeColors.infoLight,

    // Neutrals
    background: adobeColors.backgroundLight,
    backgroundAlt: adobeColors.backgroundDark,
    backgroundDark: adobeColors.backgroundDark,
    backgroundNeutral: '#F0F0F0',
    surface: adobeColors.surfaceLight,
    surfaceElevated: adobeColors.surfaceElevated,

    // Text
    textPrimary: adobeColors.darkGray900,
    textSecondary: adobeColors.mediumGray600,
    textTertiary: adobeColors.mediumGray500,
    textInverse: adobeColors.white,

    // Borders
    borderDefault: adobeColors.lightGray300,
    borderSubtle: adobeColors.lightGray200,
    borderInput: adobeColors.borderInput,
  },

  // Spacing
  spacing,

  // Z-index
  zIndex,

  // Typography
  typography,

  // Border radius
  borderRadius,

  // Shadows
  shadows,

  // Transitions
  transitions,

  // Component-specific tokens
  components: {
    button: {
      paddingX: spacing.base,
      paddingY: '10px',
      borderRadius: borderRadius.md,
      transition: transitions.default,
      fontSize: typography.fontSize.body,
      fontWeight: typography.fontWeight.semibold,
      // SYN-2738 (A9): fundos/texto do DSButton por tema (--button-*).
      primaryBg: adobeColors.primary,
      primaryBgHover: adobeColors.primaryDark,
      // C12 (2026-10-05): texto do botão ghost/de texto. Aqui = a cor primária de sempre.
      ghostText: adobeColors.primary,
      secondaryBg: adobeColors.secondary,
      secondaryBgHover: adobeColors.secondaryDark,
      accentText: adobeColors.darkGray900,
    },
    card: {
      padding: spacing.xl,
      borderRadius: borderRadius.lg,
      backgroundColor: adobeColors.surfaceLight,
      boxShadow: shadows.base,
    },
    input: {
      paddingX: spacing.base,
      paddingY: '10px',
      borderRadius: borderRadius.md,
      borderColor: adobeColors.lightGray300,
      focusBorderColor: adobeColors.primary,
      fontSize: typography.fontSize.body,
    },
    chip: {
      paddingX: spacing.md,
      paddingY: spacing.xs,
      borderRadius: borderRadius.full,
      fontSize: typography.fontSize.labelSmall,
      fontWeight: typography.fontWeight.medium,
    },
    badge: {
      paddingX: spacing.sm,
      paddingY: spacing.xs,
      borderRadius: borderRadius.sm,
      fontSize: typography.fontSize.labelSmall,
      fontWeight: typography.fontWeight.bold,
    },
    sidebar: {
      backgroundColor: adobeColors.backgroundLight,
      iconSelected: adobeColors.primary,
      iconDefault: adobeColors.mediumGray600,
      iconDisabled: adobeColors.lightGray400,
      iconStatic: adobeColors.darkGray800,
      divider: adobeColors.lightGray300,
    },
  },

  // Vuetify theme configuration
  vuetifyTheme: {
    dark: false,
    colors: {
      primary: adobeColors.primary,
      secondary: adobeColors.secondary,
      accent: adobeColors.accent,
      error: adobeColors.error,
      warning: adobeColors.warning,
      info: adobeColors.info,
      success: adobeColors.success,
      background: adobeColors.backgroundLight,
      surface: adobeColors.surfaceLight,
    },
  },
};
